import { useEffect, useState } from "react";

/**
 * useVisitorLocation
 * The visitor's country, as Toukky's own server reads it from their address
 * (GET /api/geo) — the same answer that picks the site's language and
 * currency, so a phone's dialling code can no longer disagree with them (a
 * browser-side lookup once said Germany where the server said Senegal).
 * The answer is kept in localStorage for 24 h and paints the page at once, but
 * the server is asked again on every visit: a visitor who has moved (a trip, a
 * VPN) is seen in their new country now, not a day later (owner, 2026-10-04:
 * through a VPN in Nigeria the home page still showed the United States'). The
 * server remembers each address's answer, so asking costs it nothing. The key
 * moved to v2 so an answer from the old lookup is not reused.
 *
 * Returns: { loading, country_code (ISO2, uppercased), country_name, city }
 * Falls back to null values on failure. The server gives the country only.
 */
let BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
while (BACKEND_URL.endsWith("/")) BACKEND_URL = BACKEND_URL.slice(0, -1);
const GEO_URL = `${BACKEND_URL}/api/geo`;
const CACHE_KEY = "toukky_visitor_geo_v2";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 h
const REQUEST_TIMEOUT_MS = 8000;

export default function useVisitorLocation() {
  const [state, setState] = useState({
    loading: true,
    country_code: null,
    country_name: null,
    city: null,
  });

  useEffect(() => {
    let cancelled = false;

    // 1. Paint from the cache at once
    let painted = false;
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached && cached.expires_at > Date.now()) {
          painted = true;
          setState({
            loading: false,
            country_code: cached.country_code,
            country_name: cached.country_name,
            city: cached.city,
          });
        }
      }
    } catch (_) {
      // ignore corrupt cache
    }
    // With nothing painted, no answer means no country; with the cache
    // painted, it means keep what we knew.
    const settleUnknown = () => {
      if (!painted) setState({ loading: false, country_code: null, country_name: null, city: null });
    };

    // 2. Ask the server all the same (see above)
    const controller = new AbortController();
    const deadline = setTimeout(() => {
      cancelled = true;
      controller.abort();
      settleUnknown();
    }, REQUEST_TIMEOUT_MS);
    const fetchGeo = async () => {
      try {
        const r = await fetch(GEO_URL, { cache: "no-store", credentials: "include", signal: controller.signal });
        if (!r.ok) throw new Error("geo http " + r.status);
        const d = await r.json();
        if (cancelled) return;
        if (!d) throw new Error("geo empty");
        const code = String(d.country || "").toUpperCase();
        const payload = {
          country_code: /^[A-Z]{2}$/.test(code) ? code : null,
          country_name: null,
          city: null,
        };
        // A server that cannot tell this time does not erase a known country.
        if (!payload.country_code && painted) return;
        try {
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({ ...payload, expires_at: Date.now() + CACHE_TTL_MS })
          );
        } catch (_) { /* quota — ignore */ }
        setState((prev) => (!prev.loading && prev.country_code === payload.country_code ? prev : { loading: false, ...payload }));
      } catch (e) {
        if (!cancelled) settleUnknown();
      } finally {
        clearTimeout(deadline);
      }
    };

    fetchGeo();
    return () => {
      cancelled = true;
      clearTimeout(deadline);
      controller.abort();
    };
  }, []);

  return state;
}
